import {inject, Injectable} from "@angular/core";
import {ImageService} from "@fedelta-pos/data-client";

/**
 * Retrieves the images and caches using the file system
 */

@Injectable({providedIn: "root"})
export class CachedImageService {

    private imageService: ImageService = inject(ImageService);

    private readonly fileSystem: CacheFileSystem;

    imagePromiseMap: Map<string, Promise<string>> = new Map();

    constructor() {
        console.log("--- CachedImageService");

        this.fileSystem = CachedImageService.createFileSystem();
    }

    /**
     * Retrieve an image from the server and caches the image for further calls
     * @param imageId
     * @param hash
     * @returns {Promise<string>}
     */
    public async getImage(imageId: number, hash: string): Promise<string> {

        // Look for in-progress retrieval of the image
        const key: string = imageId.toString() + ";" + hash;
        let imagePromise: Promise<string> = this.imagePromiseMap.get(key);
        if (imagePromise) {
            return imagePromise;
        }

        imagePromise = (async () => {
            let image: string;
            if (this.fileSystem) {
                try {
                    image = await this.fileSystem.getCachedImage(imageId, hash);
                } catch (error) {
                    image = this.fileSystem.cacheImage(imageId, hash, await this.imageService.getImage(imageId));
                }
            } else {
                image = await this.imageService.getImage(imageId);
            }

            // Map is only used while loading image
            this.imagePromiseMap.delete(key);

            return image;
        })();

        this.imagePromiseMap.set(key, imagePromise);
        return imagePromise;
    }

    private static createFileSystem(): CacheFileSystem {
        return new MemoryCacheFileSystem();
    }
}

interface CacheFileSystem {
    /**
     * Returns a Promise that resolves to the cached image or rejects if there is no cached image
     * @param imageId
     * @param hash
     */
    getCachedImage(imageId: number, hash: string): Promise<string>;

    /**
     * Caches the image
     * @param imageId
     * @param hash
     * @param data
     */
    cacheImage(imageId: number, hash: string, data: string): string;
}

class MemoryCacheFileSystem implements CacheFileSystem {

    mapCache: Map<string, string> = new Map<string, string>();

    async getCachedImage(imageId: number, hash: string): Promise<string> {
        const imgKey: string = imageId.toString() + "_img";
        const hashKey: string = imageId.toString() + "_hash";

        const cachedHash: string = this.mapCache.get(hashKey);
        if (!cachedHash || cachedHash != hash) {
            if (!cachedHash) {
                this.mapCache.delete(hashKey);
                this.mapCache.delete(imgKey);
            }
            throw "image not cached";
        }
        const cachedImage: string = this.mapCache.get(imgKey);
        if (!cachedImage) {
            throw "image not cached";
        }
        return cachedImage;
    }

    cacheImage(imageId: number, hash: string, data: string): string {
        const imgKey: string = imageId.toString() + "_img";
        const hashKey: string = imageId.toString() + "_hash";

        try {
            this.mapCache.set(imgKey, data);
            this.mapCache.set(hashKey, hash);
            return data;
        } catch (error) {
            console.log("Failed to cache the image");
            console.error(error);
            return undefined;
        }
    }
}
